import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2, X, Check } from 'lucide-react'

import { CalEvent, createEvent, deleteEvent, listEvents, updateEvent } from '../lib/api'

interface Props { mode: 'new' | 'edit' }

export default function EventEdit({ mode }: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { uid = '' } = useParams()
  const [searchParams] = useSearchParams()
  const dtstartParam = searchParams.get('dtstart') || ''

  const [summary, setSummary] = useState('')
  const [description, setDescription] = useState('')
  const [location, setLocation] = useState('')
  const [start, setStart] = useState(defaultDateTime())
  const [end, setEnd] = useState(defaultDateTime(60))
  const [allDay, setAllDay] = useState(false)

  const existing = useQuery({
    queryKey: ['event', uid, dtstartParam],
    queryFn: async () => {
      if (mode !== 'edit') return null
      const s = dtstartParam
      const e = new Date(new Date(dtstartParam).getTime() + 24*60*60*1000).toISOString()
      const { events } = await listEvents(s, e)
      return events.find(x => x.uid === uid) || null
    },
    enabled: mode === 'edit',
  })

  useEffect(() => {
    if (existing.data) {
      setSummary(existing.data.summary || '')
      setDescription(existing.data.description || '')
      setLocation(existing.data.location || '')
      setStart(toLocalInput(existing.data.dtstart))
      setEnd(existing.data.dtend ? toLocalInput(existing.data.dtend) : defaultDateTime(60))
      setAllDay(!!existing.data.all_day)
    }
  }, [existing.data])

  const save = useMutation({
    mutationFn: async () => {
      const payload: Partial<CalEvent> = {
        summary, description, location,
        dtstart: allDay ? start.slice(0, 10) : new Date(start).toISOString(),
        dtend: allDay ? end.slice(0, 10) : new Date(end).toISOString(),
        all_day: allDay,
      }
      if (mode === 'new') return createEvent(payload)
      return updateEvent(uid, dtstartParam, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] })
      navigate('/calendar')
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteEvent(uid, dtstartParam),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] })
      navigate('/calendar')
    },
  })

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-4 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Planner</div>
          <h1 className="text-xl font-semibold text-ink-900 tracking-tight">
            {mode === 'new' ? 'New event' : 'Edit event'}
          </h1>
        </div>
        <div className="flex gap-2">
          {mode === 'edit' && (
            <button onClick={() => remove.mutate()} className="pill-danger">
              <Trash2 size={16} /> Delete
            </button>
          )}
          <button onClick={() => navigate(-1)} className="pill-ghost">
            <X size={16} /> Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !summary}
            className="pill-primary disabled:opacity-50"
          >
            <Check size={16} />
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      <div className="p-6 max-w-2xl space-y-4">
        <input
          value={summary} onChange={e => setSummary(e.target.value)}
          placeholder="Event title" autoFocus
          className="w-full text-2xl font-semibold py-2 bg-transparent border-b border-line focus:outline-none focus:border-ink-700 text-ink-900"
        />
        <input
          value={location} onChange={e => setLocation(e.target.value)}
          placeholder="Location (optional)"
          className="field-flush"
        />

        <label className="inline-flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
          All day
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <div className="h-eyebrow mb-1">Start</div>
            <input
              type={allDay ? 'date' : 'datetime-local'}
              value={allDay ? start.slice(0, 10) : start}
              onChange={e => setStart(e.target.value)}
              className="field"
            />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1">End</div>
            <input
              type={allDay ? 'date' : 'datetime-local'}
              value={allDay ? end.slice(0, 10) : end}
              onChange={e => setEnd(e.target.value)}
              className="field"
            />
          </label>
        </div>

        <textarea
          value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Description"
          className="w-full min-h-[160px] card-inset p-4 text-sm text-ink-900 focus:outline-none focus:shadow-focus resize-none"
        />

        {save.isError && (
          <div className="text-red-700 text-sm bg-red-50 p-3 rounded-soft">
            {save.error instanceof Error ? save.error.message : String(save.error)}
          </div>
        )}
      </div>
    </div>
  )
}

function defaultDateTime(minutesOffset = 0): string {
  const d = new Date(Date.now() + minutesOffset * 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
